<script setup lang="ts">
import { computed, watch } from 'vue'
import { Form, Field } from 'vee-validate'
import { ChevronDownIcon } from '@heroicons/vue/24/outline'
import WidgetModalShell from '@/components/Dashboard/Modals/WidgetModalShell.vue'
import ButtonLoader from '@/components/Globals/Feedbacks/ButtonLoader.vue'
import type { ISensor } from '@/interfaces/entities/sensor'
import type { IWidget } from '@/interfaces/components/dashboard/widget'
import type { IWidgetFormData } from '@/interfaces/components/dashboard/widget'
import { totalizersWidgetSchema } from '@/schemas/WidgetModalSchema'
import DropdownSensors from '@/components/Globals/Inputs/DropdownSensors.vue'
import { useI18n } from '@/composable/useI18n'
import { storeToRefs } from 'pinia'
import { useCompanyStore } from '@/store/company'

type DataFieldOption = 'demand' | 'utilization' | 'consumption' | 'mixingCycle'

const props = withDefaults(
  defineProps<{
    sensors: ISensor[]
    initialData?: IWidget
    loading?: boolean
  }>(),
  { initialData: undefined, loading: false },
)

const emit = defineEmits<{
  save: [formData: IWidgetFormData]
  cancel: []
}>()

const { t } = useI18n()
const { hasMixingCyclesEnabled } = storeToRefs(useCompanyStore())

const fieldLabelClass = 'mb-1.5 block text-xs font-bold uppercase tracking-wider text-body'
const fieldInputClass =
  'w-full border border-stroke bg-gray-2 px-3 py-2.5 text-sm text-black outline-none focus:border-primary'
const fieldErrorClass = 'mt-1 text-sm text-red'

const modalTitle = computed(() =>
  props.initialData
    ? t('custom_dashboards.widget_modal.totalizers.title_edit')
    : t('custom_dashboards.widget_modal.totalizers.title_create'),
)

const selectedSensor = computed(() => {
  if (!props.initialData) return null
  const sensorId = props.initialData.config.sensor_ids[0]
  return props.sensors.find((s) => String(s.id) === sensorId) || null
})

const initialValues = computed(() => {
  if (props.initialData) {
    const sensorId = props.initialData.config.sensor_ids[0] || ''

    return {
      title: props.initialData.title,
      sensorId,
      dataField: props.initialData.config.data_field || '',
    }
  }

  return {
    title: t('custom_dashboards.widget_modal.totalizers.default_title'),
    sensorId: '',
    dataField: '',
  }
})

const dataFieldOptions = computed(() => {
  const baseOptions: DataFieldOption[] = ['demand', 'utilization', 'consumption']
  
  if (hasMixingCyclesEnabled.value) {
    baseOptions.push('mixingCycle')
  }
  
  return baseOptions.map((value) => ({
    value,
    label: t(`custom_dashboards.widget_modal.totalizers.data_fields.${value}`),
  }))
})

function dataFieldDescription(dataField: string): string {
  return t(`custom_dashboards.widget_modal.totalizers.data_field_descriptions.${dataField}`)
}

function onSensorSelected(sensor: ISensor, setFieldValue: (field: string, value: any) => void) {
  setFieldValue('sensorId', String(sensor.id))
}

function onSubmit(values: { title: string; sensorId: string; dataField: string }) {
  const formData: IWidgetFormData = {
    title: values.title,
    sensorId: values.sensorId,
    dataField: values.dataField,
  }
  emit('save', formData)
}
</script>

<template>
  <WidgetModalShell :loading="loading" @cancel="emit('cancel')">
    <template #title>
      <h2 class="font-satoshi mb-6 text-xl font-bold text-black">
        {{ modalTitle }}
      </h2>
    </template>

    <Form
      :validation-schema="totalizersWidgetSchema"
      :initial-values="initialValues"
      @submit="onSubmit"
      v-slot="{ values, setFieldValue, errors }"
    >
      <div class="space-y-5">
        <Field name="title" v-slot="{ field, errorMessage }">
          <div>
            <label :class="fieldLabelClass">
              {{ t('custom_dashboards.widget_modal.totalizers.fields.title') }}
            </label>
            <input
              v-bind="field"
              type="text"
              :class="fieldInputClass"
              :placeholder="t('custom_dashboards.widget_modal.totalizers.placeholders.title')"
            />
            <p v-if="errorMessage" :class="fieldErrorClass">{{ errorMessage }}</p>
          </div>
        </Field>

        <div>
          <label :class="fieldLabelClass">
            {{ t('custom_dashboards.widget_modal.totalizers.fields.sensor') }}
          </label>
          <DropdownSensors
            name="sensorId"
            :options="sensors"
            :already-selected-option="selectedSensor || undefined"
            :constrain-to-parent="true"
            @option-selected="(sensor: ISensor) => onSensorSelected(sensor, setFieldValue)"
          />
          <p v-if="errors.sensorId" :class="fieldErrorClass">{{ errors.sensorId }}</p>
        </div>

        <Field name="dataField" v-slot="{ field, errorMessage }">
          <div>
            <label :class="fieldLabelClass">
              {{ t('custom_dashboards.widget_modal.totalizers.fields.data_field') }}
            </label>
            <div class="relative">
              <select v-bind="field" :class="[fieldInputClass, 'appearance-none pr-10']">
                <option value="">Selecione...</option>
                <option v-for="option in dataFieldOptions" :key="option.value" :value="option.value">
                  {{ option.label }}
                </option>
              </select>
              <ChevronDownIcon
                class="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400"
              />
            </div>
            <p v-if="errorMessage" :class="fieldErrorClass">{{ errorMessage }}</p>
          </div>
        </Field>

        <div v-if="values.dataField" class="border-l-4 border-blue bg-blue-50 px-4 py-3">
          <p class="text-sm leading-relaxed text-body">
            {{ dataFieldDescription(values.dataField as string) }}
          </p>
        </div>
      </div>

      <div class="mt-6 border-t border-stroke pt-6">
        <div class="flex justify-end gap-4">
          <button
            type="button"
            class="text-xs font-medium uppercase tracking-wide text-body hover:text-black disabled:cursor-not-allowed disabled:opacity-50"
            :disabled="loading"
            @click="emit('cancel')"
          >
            {{ t('custom_dashboards.widget_modal.totalizers.actions.cancel') }}
          </button>
          <button
            type="submit"
            class="flex h-10 min-w-[7rem] items-center justify-center bg-blue px-6 py-2.5 text-xs font-medium uppercase tracking-wide text-white hover:bg-blue-600 disabled:cursor-not-allowed disabled:opacity-50"
            :disabled="loading"
          >
            <ButtonLoader v-if="loading" />
            <span v-else>
              {{
                initialData
                  ? t('custom_dashboards.widget_modal.totalizers.actions.save')
                  : t('custom_dashboards.widget_modal.totalizers.actions.create')
              }}
            </span>
          </button>
        </div>
      </div>
    </Form>
  </WidgetModalShell>
</template>

<style scoped>
.font-satoshi {
  font-family: 'Satoshi', sans-serif;
}
</style>
